import axios from 'axios';
import { toast } from 'react-toastify';

const BASE = 'https://saviagent.savictech.com/journal-posting/api/v1';

/* ── Azure AD SSO via backend redirect ──
   Flow:
   1. Frontend redirects → backend /auth/azure/login?frontend_redirect=<this page>
   2. Backend handles Azure AD OAuth, generates its own JWT
   3. Backend redirects → <this page>?access_token=<jwt>&token_type=bearer
   4. JournalApp reads token from URL, stores it, clears URL
*/
export const getAzureLoginUrl = () => {
  const redirectBack = `${window.location.origin}/journal`;
  return `${BASE}/auth/azure/login?frontend_redirect=${encodeURIComponent(redirectBack)}`;
};

/* ── Token & profile storage ── */
const TOKEN_KEY   = 'journal_access_token';
const PROFILE_KEY = 'journal_user_profile';

export const getToken      = ()  => localStorage.getItem(TOKEN_KEY);
export const setToken      = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken    = ()  => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(PROFILE_KEY);
};

export const setUserProfile = (p) => localStorage.setItem(PROFILE_KEY, JSON.stringify(p));
export const getUserProfile = () => {
  try { return JSON.parse(localStorage.getItem(PROFILE_KEY)); } catch { return null; }
};

export const parseJwt = (token) => {
  try { return JSON.parse(atob(token.split('.')[1])); } catch { return null; }
};

// Returns user object — prefers stored profile (from /auth/me), falls back to JWT decode
export const getCurrentUser = () => {
  const profile = getUserProfile();
  if (profile) return profile;
  const t = getToken();
  return t ? parseJwt(t) : null;
};

export const getUserRole         = () => getCurrentUser()?.role         || null;
export const getUserEmail        = () => getCurrentUser()?.email        || getCurrentUser()?.sub   || '';
export const getUserDisplayName  = () => getCurrentUser()?.display_name || getCurrentUser()?.email || getCurrentUser()?.sub || '';
export const getUserCompanyCodes = () => getCurrentUser()?.company_codes || [];

export const canApprove     = () => ['finance_manager','financial_controller','cfo'].includes(getUserRole());
export const canAccessConfig = () => ['finance_admin','financial_controller','cfo'].includes(getUserRole());
export const canManageUsers  = () => getUserRole() === 'finance_admin';
export const canSubmitEntry  = () => !['internal_auditor'].includes(getUserRole());

export const isTokenExpired = () => {
  const t = getToken();
  if (!t) return true;
  const payload = parseJwt(t);
  if (!payload?.exp) return false; // no expiry field — treat as valid
  return Date.now() / 1000 > payload.exp;
};

/* ── Axios instance ── */
const api = axios.create({ baseURL: BASE });

api.interceptors.request.use(config => {
  const token = getToken();
  if (token) config.headers['Authorization'] = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  err => {
    const status = err.response?.status;
    const data   = err.response?.data;

    if (status === 401) {
      // Only fire global logout if a token actually exists (avoids clearing a valid
      // token when an unrelated 401 fires during the login / getMe flow)
      if (getToken()) {
        clearToken();
        window.dispatchEvent(new Event('journal:logout'));
      }
      return Promise.reject(err);
    }
    if (status === 403) {
      toast.error(data?.message || "You don't have permission for this action.");
      return Promise.reject(err);
    }
    if (status === 409) {
      toast.error('Duplicate request — this entry was already submitted.');
      return Promise.reject(err);
    }
    if (status === 422) return Promise.reject(err);
    if (status === 429) { toast.error('Rate limit reached. Please try again later.'); return Promise.reject(err); }
    if (status === 404) { toast.error('Record not found.'); return Promise.reject(err); }
    if (status === 500) { toast.error('Server error. Please contact support.'); return Promise.reject(err); }
    return Promise.reject(err);
  }
);

/* ── Auth endpoints ── */
// Username/password — for local dev / non-AAD backends
export const login = (username, password) => {
  const form = new URLSearchParams();
  form.append('username', username);
  form.append('password', password);
  return axios.post(`${BASE}/auth/token`, form, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
};

// GET /auth/me — uses raw axios so a 401 here never triggers the global logout interceptor
export const getMe = () => {
  const token = getToken();
  return axios.get(`${BASE}/auth/me`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

/* ── Reports / Dashboard ── */
export const getDashboard        = (cc)      => api.get('/reports/dashboard',         { params: { company_code: cc } });
export const getApprovalSla      = ()        => api.get('/reports/approval-sla');
export const getValidationErrors = (days,cc) => api.get('/reports/validation-errors', { params: { days, company_code: cc } });
export const getVolumeReport     = (params)  => api.get('/reports/volume',            { params });

/* ── Entries ── */
export const submitEntry  = (payload)        => api.post('/entries', payload);
export const getEntries   = (params)         => api.get('/entries',             { params });
export const getEntry     = (trackingId)     => api.get(`/entries/${trackingId}`);
export const patchEntry   = (trackingId, d)  => api.patch(`/entries/${trackingId}`, d);
export const resubmitEntry= (trackingId)     => api.post(`/entries/${trackingId}/resubmit`);
export const cancelEntry  = (trackingId)     => api.delete(`/entries/${trackingId}`);

export const bulkUpload = (file, onUploadProgress) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/entries/bulk', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress,
  });
};

/* ── Approvals ── */
export const getApprovals = (params) => api.get('/approvals', { params });
export const getApproval  = (id)     => api.get(`/approvals/${id}`);

export const submitApprovalAction = (id, payload) =>
  api.post(`/approvals/${id}/action`, payload);

/* ── Master Data ── */
export const getCostCenters   = (cc) => api.get('/masters/cost-centers',  { params: { company_code: cc } });
export const getProfitCenters = (cc) => api.get('/masters/profit-centers', { params: { company_code: cc } });
export const getWbsElements   = (cc) => api.get('/masters/wbs-elements',   { params: { company_code: cc } });

// Raw axios — bypasses the 404 toast interceptor so inline validation messages show instead
export const validateCostCenter = (costCenter, cc) => {
  const token = getToken();
  return axios.get(`${BASE}/masters/cost-centers/${encodeURIComponent(costCenter)}`, {
    params: { company_code: cc },
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};
export const validateProfitCenter = (profitCenter, cc) => {
  const token = getToken();
  return axios.get(`${BASE}/masters/profit-centers/${encodeURIComponent(profitCenter)}`, {
    params: { company_code: cc },
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

/* ── Config ── */
export const getConfigRules = ()        => api.get('/config/rules');
export const saveConfigRule = (payload) => api.put('/config/rules', payload);

/* ── Users (finance_admin only) ── */
export const getUsers    = ()         => api.get('/users');
export const createUser  = (payload)  => api.post('/users', payload);
export const getUserById = (id)       => api.get(`/users/${id}`);
export const updateUser  = (id, data) => api.patch(`/users/${id}`, data);
export const deleteUser  = (id)       => api.delete(`/users/${id}`);
